import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button, Card } from "../components";
import { useAuth } from "../contexts/AuthContext";
import { apiPost, apiGet } from "../lib/api";
import type { Subscription, ReferralValidation } from "@jumpbean/shared";

type BillingPeriod = "monthly" | "annual";
type PriceId =
  | "family_monthly"
  | "family_annual"
  | "classroom_monthly"
  | "classroom_annual";

interface PricingTier {
  name: string;
  tier: "free" | "family" | "classroom";
  monthlyPrice: number;
  annualPrice: number;
  annualMonthlyPrice: number;
  annualSavingsPercent: number;
  description: string;
  features: string[];
  highlighted?: boolean;
  childLimit: number;
}

const TIERS: PricingTier[] = [
  {
    name: "Free",
    tier: "free",
    monthlyPrice: 0,
    annualPrice: 0,
    annualMonthlyPrice: 0,
    annualSavingsPercent: 0,
    description: "Start the adventure",
    childLimit: 1,
    features: [
      "Level 1 of all 5 disciplines",
      "1 child profile",
      "All magical character guides",
      "Stars & badges for completed levels",
      "Basic progress tracking",
    ],
  },
  {
    name: "Family",
    tier: "family",
    monthlyPrice: 5,
    annualPrice: 48,
    annualMonthlyPrice: 4,
    annualSavingsPercent: 20,
    description: "Unlock the full journey",
    childLimit: 4,
    highlighted: true,
    features: [
      "All 5 levels per discipline",
      "Up to 4 child profiles",
      "Detailed progress reports",
      "Parent dashboard",
      "All badges & rewards",
      "Priority support",
    ],
  },
  {
    name: "Classroom",
    tier: "classroom",
    monthlyPrice: 29,
    annualPrice: 290,
    annualMonthlyPrice: 24.17,
    annualSavingsPercent: 17,
    description: "For educators & schools",
    childLimit: 35,
    features: [
      "Everything in Family",
      "Up to 35 students",
      "Class management tools",
      "Student grouping",
      "Exportable progress reports",
      "Bulk student import",
    ],
  },
];

/**
 * PricingPage - Beautiful pricing page with monthly/annual toggle,
 * Stripe checkout integration, and current plan display.
 */
export function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("annual");
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);

  // Referral code state
  const [referralCode, setReferralCode] = useState("");
  const [referralInput, setReferralInput] = useState("");
  const [referralValidation, setReferralValidation] = useState<ReferralValidation | null>(null);
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);
  const [referralError, setReferralError] = useState<string | null>(null);

  // Validate a referral code
  const validateReferralCode = useCallback(async (code: string) => {
    if (!code.trim()) return;

    setIsValidatingReferral(true);
    setReferralError(null);

    try {
      const { data, error } = await apiPost<ReferralValidation>(
        "/api/referrals/validate",
        { code: code.trim().toUpperCase() }
      );

      if (error) {
        setReferralError(error.message);
        setReferralValidation(null);
        return;
      }

      if (data?.valid) {
        setReferralCode(code.trim().toUpperCase());
        setReferralValidation(data);
        // Store in localStorage for persistence
        localStorage.setItem("referral_code", code.trim().toUpperCase());
      } else {
        setReferralError(data?.reason || "Invalid referral code");
        setReferralValidation(null);
      }
    } catch {
      setReferralError("Failed to validate referral code");
      setReferralValidation(null);
    } finally {
      setIsValidatingReferral(false);
    }
  }, []);

  // Remove applied referral code
  const removeReferralCode = useCallback(() => {
    setReferralCode("");
    setReferralInput("");
    setReferralValidation(null);
    setReferralError(null);
    localStorage.removeItem("referral_code");
  }, []);

  // Check for referral code from URL params or localStorage on mount
  useEffect(() => {
    const urlCode = searchParams.get("ref");
    const storedCode = localStorage.getItem("referral_code");

    const codeToValidate = urlCode || storedCode;
    if (codeToValidate) {
      setReferralInput(codeToValidate);
      validateReferralCode(codeToValidate);
    }
  }, [searchParams, validateReferralCode]);

  // Fetch current subscription if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscription();
    }
  }, [isAuthenticated]);

  async function fetchSubscription() {
    setIsLoadingSubscription(true);
    try {
      const { data, error } = await apiGet<{ subscription: Subscription | null }>(
        "/api/billing/subscription"
      );
      if (!error && data?.subscription) {
        setSubscription(data.subscription);
      }
    } catch {
      // Subscription fetch failed, user may not have one
    } finally {
      setIsLoadingSubscription(false);
    }
  }

  async function handleSubscribe(tier: PricingTier) {
    setError(null);

    // For free tier, redirect to register
    if (tier.tier === "free") {
      navigate("/register");
      return;
    }

    // If not authenticated, redirect to register first
    if (!isAuthenticated) {
      navigate("/register", { state: { returnTo: "/pricing" } });
      return;
    }

    // Build price ID
    const priceId: PriceId = `${tier.tier}_${billingPeriod}` as PriceId;

    setLoadingTier(tier.tier);

    try {
      const { data, error } = await apiPost<{ url: string }>(
        "/api/billing/create-checkout-session",
        {
          priceId,
          successUrl: `${window.location.origin}/profiles?subscription=success`,
          cancelUrl: `${window.location.origin}/pricing?subscription=canceled`,
          // Pass referral code if valid (only for Family tier)
          referralCode: referralValidation?.valid && tier.tier === "family" ? referralCode : undefined,
        }
      );

      if (error) {
        setError(error.message);
        setLoadingTier(null);
        return;
      }

      if (data?.url) {
        // Clear referral code from localStorage on successful checkout redirect
        // (will be processed by Stripe webhook)
        localStorage.removeItem("referral_code");
        window.location.href = data.url;
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setLoadingTier(null);
    }
  }

  async function handleManageBilling() {
    setError(null);
    setLoadingTier("manage");

    try {
      const { data, error } = await apiPost<{ url: string }>(
        "/api/billing/create-portal-session",
        {
          returnUrl: `${window.location.origin}/pricing`,
        }
      );

      if (error) {
        setError(error.message);
        setLoadingTier(null);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setLoadingTier(null);
    }
  }

  function getButtonText(tier: PricingTier): string {
    if (tier.tier === "free") {
      return isAuthenticated ? "Current Plan" : "Get Started Free";
    }

    // Check if this is the current plan
    if (subscription?.tier === tier.tier && subscription?.status === "active") {
      return "Current Plan";
    }

    if (tier.tier === "family") {
      return "Start 7-Day Trial";
    }

    return "Subscribe";
  }

  function isCurrentPlan(tier: PricingTier): boolean {
    if (tier.tier === "free") {
      return isAuthenticated && (!subscription || subscription.tier === "free");
    }
    return subscription?.tier === tier.tier && subscription?.status === "active";
  }

  function formatPrice(price: number): string {
    if (price === 0) return "$0";
    if (Number.isInteger(price)) return `$${price}`;
    return `$${price.toFixed(2)}`;
  }

  const showManageBilling =
    isAuthenticated &&
    subscription &&
    subscription.tier !== "free" &&
    subscription.stripeCustomerId;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero Section with floating decorations */}
      <div className="relative overflow-hidden bg-gradient-to-b from-magic-50 via-fairy-50 to-cloud pt-12 pb-16 px-4">
        {/* Floating decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <FloatingShape
            className="absolute top-20 left-[10%] w-16 h-16 bg-sunshine-200 rounded-full opacity-60"
            delay={0}
          />
          <FloatingShape
            className="absolute top-32 right-[15%] w-12 h-12 bg-meadow-200 rounded-full opacity-50"
            delay={0.5}
          />
          <FloatingShape
            className="absolute top-48 left-[20%] w-8 h-8 bg-fairy-200 rounded-full opacity-40"
            delay={1}
          />
          <FloatingShape
            className="absolute top-24 right-[25%] w-20 h-20 bg-magic-200 rounded-full opacity-30"
            delay={1.5}
          />
          <FloatingShape
            className="absolute top-56 right-[10%] w-10 h-10 bg-sky-200 rounded-full opacity-50"
            delay={0.75}
          />

          {/* Stars */}
          <StarDecoration className="absolute top-16 left-[30%] text-sunshine-400" size={24} delay={0.2} />
          <StarDecoration className="absolute top-40 right-[30%] text-sunshine-400" size={16} delay={0.8} />
          <StarDecoration className="absolute top-28 left-[50%] text-sunshine-300" size={20} delay={1.2} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="font-display font-extrabold text-display-lg md:text-display-xl text-magic-600 mb-4 leading-tight">
            Simple, Magical Pricing
          </h1>
          <p className="font-body text-body-lg md:text-body-xl text-neutral-600 max-w-2xl mx-auto mb-10">
            Start free and unlock more adventures when you're ready.
            <span className="block mt-2 text-magic-500 font-semibold">
              Children never see paywalls — just pure learning magic!
            </span>
          </p>

          {/* Billing Period Toggle */}
          <BillingToggle
            value={billingPeriod}
            onChange={setBillingPeriod}
          />
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="flex-1 bg-gradient-to-b from-cloud to-neutral-50 px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Error message */}
          {error && (
            <div className="mb-8 max-w-md mx-auto p-4 bg-fairy-100 border-2 border-fairy-300 rounded-2xl text-center">
              <p className="font-body text-body-md text-fairy-700">{error}</p>
            </div>
          )}

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {TIERS.map((tier, index) => (
              <PricingCard
                key={tier.tier}
                tier={tier}
                billingPeriod={billingPeriod}
                isCurrentPlan={isCurrentPlan(tier)}
                isLoading={loadingTier === tier.tier}
                isLoadingSubscription={isLoadingSubscription || authLoading}
                buttonText={getButtonText(tier)}
                formatPrice={formatPrice}
                onSubscribe={() => handleSubscribe(tier)}
                index={index}
                referralDiscount={referralValidation?.valid && tier.tier === "family" ? referralValidation : null}
              />
            ))}
          </div>

          {/* Referral Code Section */}
          <ReferralCodeSection
            referralCode={referralCode}
            referralInput={referralInput}
            referralValidation={referralValidation}
            referralError={referralError}
            isValidating={isValidatingReferral}
            onInputChange={setReferralInput}
            onApply={() => validateReferralCode(referralInput)}
            onRemove={removeReferralCode}
          />

          {/* Manage Billing Button */}
          {showManageBilling && (
            <div className="mt-10 text-center">
              <button
                onClick={handleManageBilling}
                disabled={loadingTier === "manage"}
                className="inline-flex items-center gap-2 font-body font-semibold text-body-md text-magic-600 hover:text-magic-700 transition-colors disabled:opacity-50"
              >
                {loadingTier === "manage" ? (
                  <>
                    <LoadingSpinner className="w-5 h-5" />
                    Opening billing portal...
                  </>
                ) : (
                  <>
                    <SettingsIcon className="w-5 h-5" />
                    Manage Billing & Subscription
                  </>
                )}
              </button>
            </div>
          )}

          {/* Trust signals */}
          <div className="mt-16 text-center">
            <div className="flex flex-wrap justify-center gap-8 text-neutral-500">
              <TrustBadge icon={<ShieldIcon />} text="Secure payment" />
              <TrustBadge icon={<RefundIcon />} text="7-day free trial" />
              <TrustBadge icon={<CancelIcon />} text="Cancel anytime" />
              <TrustBadge icon={<HeartIcon />} text="No ads, ever" />
            </div>
          </div>

          {/* FAQ teaser */}
          <div className="mt-16 max-w-2xl mx-auto text-center">
            <h2 className="font-display font-bold text-display-sm text-magic-600 mb-4">
              Questions?
            </h2>
            <p className="font-body text-body-lg text-neutral-600 mb-6">
              We're here to help! Reach out anytime.
            </p>
            <Link
              to="/contact"
              className="font-body font-semibold text-body-md text-magic-500 hover:text-magic-600 transition-colors"
            >
              Contact Support →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface BillingToggleProps {
  value: BillingPeriod;
  onChange: (value: BillingPeriod) => void;
}

function BillingToggle({ value, onChange }: BillingToggleProps) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="inline-flex items-center gap-1 p-1.5 bg-cloud rounded-full shadow-lg border-2 border-magic-100">
        <button
          onClick={() => onChange("monthly")}
          className={`
            relative px-6 py-3 rounded-full font-display font-bold text-body-md transition-all duration-300
            ${
              value === "monthly"
                ? "bg-magic-500 text-white shadow-md"
                : "text-neutral-600 hover:text-magic-600"
            }
          `}
        >
          Monthly
        </button>
        <button
          onClick={() => onChange("annual")}
          className={`
            relative px-6 py-3 rounded-full font-display font-bold text-body-md transition-all duration-300
            ${
              value === "annual"
                ? "bg-magic-500 text-white shadow-md"
                : "text-neutral-600 hover:text-magic-600"
            }
          `}
        >
          Annual
          <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-meadow-400 text-white text-xs font-bold rounded-full shadow-sm">
            Save 20%
          </span>
        </button>
      </div>
      <p className="font-body text-body-sm text-meadow-600 font-medium">
        Most families choose annual
      </p>
    </div>
  );
}

interface PricingCardProps {
  tier: PricingTier;
  billingPeriod: BillingPeriod;
  isCurrentPlan: boolean;
  isLoading: boolean;
  isLoadingSubscription: boolean;
  buttonText: string;
  formatPrice: (price: number) => string;
  onSubscribe: () => void;
  index: number;
  referralDiscount: ReferralValidation | null;
}

function PricingCard({
  tier,
  billingPeriod,
  isCurrentPlan,
  isLoading,
  isLoadingSubscription,
  buttonText,
  formatPrice,
  onSubscribe,
  referralDiscount,
}: PricingCardProps) {
  const price =
    billingPeriod === "monthly" ? tier.monthlyPrice : tier.annualMonthlyPrice;
  const isHighlighted = tier.highlighted;
  const isFree = tier.tier === "free";
  const hasReferralDiscount = !!referralDiscount?.valid;

  return (
    <div
      className={`
        relative flex flex-col
        transition-all duration-500 ease-out
        ${isHighlighted || isCurrentPlan ? "pt-4" : ""}
      `}
    >
      {/* Popular badge - positioned above the card */}
      {isHighlighted && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="px-5 py-1.5 bg-gradient-to-r from-sunshine-400 via-sunshine-500 to-sunshine-400 text-neutral-900 font-display font-bold text-sm rounded-full shadow-lg border-2 border-sunshine-600">
            Most Popular
          </div>
        </div>
      )}

      {/* Current plan badge - positioned above the card */}
      {isCurrentPlan && !isLoadingSubscription && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="px-5 py-1.5 bg-meadow-500 text-white font-display font-bold text-sm rounded-full shadow-lg">
            Your Plan
          </div>
        </div>
      )}

      <Card
        variant="elevated"
        className={`
          flex-1 flex flex-col p-6 lg:p-8
          ${
            isHighlighted
              ? "border-2 border-magic-400 shadow-[0_8px_32px_rgba(155,89,182,0.2)]"
              : ""
          }
          ${isCurrentPlan ? "border-2 border-meadow-400 bg-meadow-50/30" : ""}
        `}
      >
        {/* Tier name */}
        <h2 className="font-display font-bold text-display-sm text-magic-600 mb-1">
          {tier.name}
        </h2>
        <p className="font-body text-body-md text-neutral-500 mb-6">
          {tier.description}
        </p>

        {/* Price */}
        <div className="mb-6">
          {hasReferralDiscount ? (
            // Price with referral discount
            <>
              <div className="flex items-baseline gap-2">
                <span className="font-display font-extrabold text-display-md text-neutral-400 line-through">
                  {formatPrice(price)}
                </span>
                <span className="font-display font-extrabold text-display-md text-meadow-600">
                  $0
                </span>
                <span className="font-body text-body-md text-neutral-500">
                  first month
                </span>
              </div>
              <p className="mt-1 font-body text-body-sm text-neutral-600">
                Then {formatPrice(price)}/month
                {billingPeriod === "annual" && (
                  <span className="ml-1">({formatPrice(tier.annualPrice)} billed annually)</span>
                )}
              </p>
            </>
          ) : (
            // Regular price display
            <>
              <div className="flex items-baseline gap-1">
                <span className="font-display font-extrabold text-display-md text-neutral-900">
                  {formatPrice(price)}
                </span>
                {!isFree && (
                  <span className="font-body text-body-md text-neutral-500">
                    /month
                  </span>
                )}
              </div>

              {/* Annual billing note */}
              {!isFree && billingPeriod === "annual" && (
                <p className="mt-1 font-body text-body-sm text-meadow-600">
                  {formatPrice(tier.annualPrice)} billed annually
                  <span className="ml-2 px-2 py-0.5 bg-meadow-100 text-meadow-700 rounded-full text-xs font-semibold">
                    Save {tier.annualSavingsPercent}%
                  </span>
                </p>
              )}

              {isFree && (
                <p className="mt-1 font-body text-body-sm text-neutral-500">
                  Free forever
                </p>
              )}
            </>
          )}
        </div>

        {/* Child limit */}
        <div className="flex items-center gap-2 mb-6 p-3 bg-fairy-50 rounded-xl">
          <ChildIcon className="w-5 h-5 text-fairy-500" />
          <span className="font-body text-body-md text-fairy-700">
            {tier.childLimit === 1
              ? "1 child profile"
              : `Up to ${tier.childLimit} ${tier.tier === "classroom" ? "students" : "children"}`}
          </span>
        </div>

        {/* Features */}
        <ul className="flex-1 space-y-3 mb-8">
          {tier.features.map((feature) => (
            <li
              key={feature}
              className="flex items-start gap-3 font-body text-body-md text-neutral-700"
            >
              <CheckIcon className="w-5 h-5 text-meadow-500 flex-shrink-0 mt-0.5" />
              {feature}
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <Button
          variant={isHighlighted && !isCurrentPlan ? "primary" : "secondary"}
          fullWidth
          size="lg"
          onClick={onSubscribe}
          disabled={isCurrentPlan || isLoading || isLoadingSubscription}
          isLoading={isLoading}
        >
          {isLoadingSubscription
            ? "Loading..."
            : hasReferralDiscount
              ? "Start Free Month"
              : buttonText}
        </Button>
      </Card>
    </div>
  );
}

interface ReferralCodeSectionProps {
  referralCode: string;
  referralInput: string;
  referralValidation: ReferralValidation | null;
  referralError: string | null;
  isValidating: boolean;
  onInputChange: (value: string) => void;
  onApply: () => void;
  onRemove: () => void;
}

function ReferralCodeSection({
  referralCode,
  referralInput,
  referralValidation,
  referralError,
  isValidating,
  onInputChange,
  onApply,
  onRemove,
}: ReferralCodeSectionProps) {
  const hasValidCode = referralValidation?.valid && referralCode;

  return (
    <div className="mt-10 max-w-lg mx-auto">
      {hasValidCode ? (
        // Discount applied state
        <div className="p-4 bg-meadow-50 border-2 border-meadow-300 rounded-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🎉</span>
              <div>
                <p className="font-body font-semibold text-body-md text-meadow-700">
                  Referral discount applied!
                </p>
                <p className="font-body text-body-sm text-meadow-600 mt-1">
                  Your first month is FREE
                  {referralValidation?.referrerName && (
                    <> (code: {referralCode})</>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onRemove}
              className="font-body text-body-sm text-neutral-500 hover:text-neutral-700 transition-colors underline"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        // Input state
        <div className="p-4 bg-neutral-50 border-2 border-neutral-200 rounded-2xl">
          <p className="font-body font-medium text-body-md text-neutral-700 mb-3">
            Have a referral code?
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={referralInput}
              onChange={(e) => onInputChange(e.target.value.toUpperCase())}
              placeholder="Enter code"
              className="flex-1 px-4 py-2 font-body text-body-md border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-magic-400 transition-colors uppercase"
              maxLength={12}
              onKeyDown={(e) => {
                if (e.key === "Enter" && referralInput.trim()) {
                  onApply();
                }
              }}
            />
            <Button
              variant="secondary"
              size="md"
              onClick={onApply}
              disabled={!referralInput.trim() || isValidating}
              isLoading={isValidating}
            >
              Apply
            </Button>
          </div>
          {referralError && (
            <p className="mt-2 font-body text-body-sm text-fairy-600">
              {referralError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

interface TrustBadgeProps {
  icon: React.ReactNode;
  text: string;
}

function TrustBadge({ icon, text }: TrustBadgeProps) {
  return (
    <div className="flex items-center gap-2 font-body text-body-md">
      <span className="text-meadow-500">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

interface FloatingShapeProps {
  className: string;
  delay: number;
}

function FloatingShape({ className, delay }: FloatingShapeProps) {
  return (
    <div
      className={`${className} animate-float`}
      style={{ animationDelay: `${delay}s` }}
    />
  );
}

interface StarDecorationProps {
  className: string;
  size: number;
  delay: number;
}

function StarDecoration({ className, size, delay }: StarDecorationProps) {
  return (
    <svg
      className={`${className} animate-sparkle`}
      style={{ animationDelay: `${delay}s` }}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

// ============================================================================
// Icons
// ============================================================================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChildIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function RefundIcon() {
  return (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

function CancelIcon() {
  return (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default PricingPage;
